import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { ITcProductComponentInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { formatRefCodeLabel } from "../../../../tc-service/ref-code-label.utils";

interface ComponentInfoProps {
  formRef: React.MutableRefObject<any>;
  componentInfoData: ITcProductComponentInformation;
  setComponentInfoData: (data: ITcProductComponentInformation) => void;
  productCategoryCode?: string;
  productDetailCode?: string;
  isReturn?: boolean;
}

const disallowedPlaceholderValues = new Set(["na", "n/a", "not available", "null"]);

const isBlankOrPlaceholder = (value: unknown) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized || disallowedPlaceholderValues.has(normalized);
};

const COMPONENT_FABRIC_CONSTRUCT_CATEGORIES = new Set([
  "PC0025",
  "PC0026",
  "PC0027",
  "PC0028",
  "PC0039",
]);

const COMPONENT_FIBER_LENGTH_CATEGORIES = new Set([
  "PC0032",
  "PC0033",
  "PC0034",
  "PC0034",
]);

const COMPONENT_YARN_COUNT_CATEGORIES = new Set([
  "PC0029",
  "PC0030",
  "PC0031",
]);

const COMPONENT_FINISHED_FABRIC_CATEGORIES = new Set([
  "PC0001", "PC0002", "PC0003", "PC0004", "PC0005", "PC0006",
  "PC0007", "PC0008", "PC0009", "PC0010", "PC0015", "PC0018",
  "PC0019", "PC0021",
]);

const COMPONENT_FINISHED_FABRIC_DETAILS = new Set([
  "PD0001", "PD0002", "PD0003", "PD0004", "PD0005", "PD0006",
  "PD0007", "PD0008", "PD0009", "PD0010", "PD0011", "PD0012",
  "PD0013", "PD0014", "PD0015", "PD0016", "PD0017", "PD0019",
  "PD0020", "PD0021", "PD0022", "PD0023", "PD0024", "PD0026",
  "PD0028", "PD0029", "PD0042", "PD0043", "PD0044", "PD0051",
]);

/**
 * C-Step 1 — Component Information.
 * Required: tcProductComponentNo, tcProductComponentDetail, tcProductComponentNetShippingWeight.
 * Optional: fiber length / yarn count / fabric weight + construct / fabric weight of finished
 * product / technical specifications / additional. Mirrors the product-info FormBuilder pattern;
 * the parent component modal drives validateForm/submitForm via the ref.
 */
const ComponentInfo: React.FC<ComponentInfoProps> = ({
  formRef,
  componentInfoData,
  setComponentInfoData,
  productCategoryCode = "",
  productDetailCode = "",
  isReturn = false,
}) => {
  // DAT-501 row 87: Component Detail is a dropdown of PD codes from ref data —
  // it was an open text box that let arbitrary values through (86ajey3rj).
  const [componentDetailOptions, setComponentDetailOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    const fetchDetailCodes = async () => {
      try {
        const result: any = await dtrackItService.getProductDetailsCode();
        if (cancelled) return;
        const options = (result?.data || []).map((t: any) => ({
          value: t.code_1,
          label: formatRefCodeLabel(t.description_1, t.code_1),
        }));
        setComponentDetailOptions(options);
      } catch (error) {
        console.error("Error fetching PD codes for component detail:", error);
      }
    };
    fetchDetailCodes();
    return () => {
      cancelled = true;
    };
  }, []);

  const validationSchema = Yup.object().shape({
    tcProductComponentNo: Yup.string()
      .required("Component Number is required")
      .test(
        "not-blank-or-placeholder",
        "Component Number cannot be blank, na, n/a, not available, or null",
        (value: string | undefined) => !isBlankOrPlaceholder(value)
      )
      .max(50, "Maximum 50 characters allowed"),
    tcProductComponentDetail: Yup.string()
      .required("Component Detail is required")
      .test(
        "not-blank-or-placeholder",
        "Component Detail cannot be blank, na, n/a, not available, or null",
        (value: string | undefined) => !isBlankOrPlaceholder(value)
      )
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentNetShippingWeight: Yup.string()
      .required("Component Net Shipping Weight is required")
      .test(
        "is-max-five-decimal-places",
        "Component Net Shipping Weight must be a valid decimal number with up to 5 decimal places.",
        (value: string | undefined) => {
          if (value === undefined || value === null || value === "") return false;
          return /^(0|[0-9]\d*|0\.\d{1,5}|[0-9]\d*\.\d{1,5})$/.test(String(value));
        }
      )
      .test(
        "greater-than-zero",
        "Component Net Shipping Weight must be greater than 0",
        (value: string | undefined) => {
          const num = parseFloat(String(value));
          return !isNaN(num) && num > 0;
        }
      ),
    tcProductComponentInfoFiberLength: Yup.string()
      .when([], {
        is: () => COMPONENT_FIBER_LENGTH_CATEGORIES.has(productCategoryCode),
        then: Yup.string()
          .required("Component Info Fiber Length is required")
          .test(
            "not-blank-or-placeholder",
            "Component Info Fiber Length cannot be blank, na, n/a, not available, or null",
            (value) => !isBlankOrPlaceholder(value)
          ),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoYarnCount: Yup.string()
      .when([], {
        is: () => COMPONENT_YARN_COUNT_CATEGORIES.has(productCategoryCode),
        then: Yup.string()
          .required("Component Info Yarn Count is required")
          .test(
            "not-blank-or-placeholder",
            "Component Info Yarn Count cannot be blank, na, n/a, not available, or null",
            (value) => !isBlankOrPlaceholder(value)
          ),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightAndConstruct: Yup.string()
      .when([], {
        is: () => COMPONENT_FABRIC_CONSTRUCT_CATEGORIES.has(productCategoryCode),
        then: Yup.string()
          .required("Component Info Fabric Weight and Construct is required")
          .test(
            "not-blank-or-placeholder",
            "Component Info Fabric Weight and Construct cannot be blank, na, n/a, not available, or null",
            (value: string | undefined) => !isBlankOrPlaceholder(value)
          ),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoFabricWeightOfFinishedProduct: Yup.string()
      .when([], {
        is: () =>
          COMPONENT_FINISHED_FABRIC_CATEGORIES.has(productCategoryCode) &&
          COMPONENT_FINISHED_FABRIC_DETAILS.has(productDetailCode),
        then: Yup.string()
          .required("Component Info Fabric Weight of Finished Product is required")
          .test(
            "not-blank-or-placeholder",
            "Component Info Fabric Weight of Finished Product cannot be blank, na, n/a, not available, or null",
            (value: string | undefined) => !isBlankOrPlaceholder(value)
          ),
        otherwise: Yup.string().nullable(),
      })
      .max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoTechnicalSpecifications: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
    tcProductComponentInfoAdditional: Yup.string().nullable().max(255, "Maximum 255 characters allowed"),
  });

  const formConfig = {
    pageName: "certified-products-component",
    subPageName: "tcProductComponentInformation",
    initialValues: componentInfoData,
    enableReinitialize: true,
    validationSchema,
    sections: [
      {
        title: "Component Information",
        fields: [
          {
            label: "Component Number",
            name: "tcProductComponentNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentNo,
            readOnly: isReturn,
            props: {
              placeholder: "Enter component number",
              disabled: isReturn,
            },
          },
          {
            label: "Component Detail",
            name: "tcProductComponentDetail",
            // DAT-501 row 87: dropdown of PD codes from ref data (86ajey3rj)
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentDetail,
            readOnly: isReturn,
            disabled: isReturn,
            props: {
              enableSearch: true,
              options: componentDetailOptions,
              placeholder: "Select component detail",
              disabled: isReturn,
            },
          },
          {
            label: "Component Net Shipping Weight",
            name: "tcProductComponentNetShippingWeight",
            type: "number",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: componentInfoData.tcProductComponentNetShippingWeight,
            props: { type: "number", placeholder: "Enter component net shipping weight" },
          },
          {
            label: "Component Info Fiber Length (mm)",
            name: "tcProductComponentInfoFiberLength",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: COMPONENT_FIBER_LENGTH_CATEGORIES.has(productCategoryCode),
            value: componentInfoData.tcProductComponentInfoFiberLength,
            props: { placeholder: "Enter fiber length" },
          },
          {
            label: "Component Info Yarn Count",
            name: "tcProductComponentInfoYarnCount",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: COMPONENT_YARN_COUNT_CATEGORIES.has(productCategoryCode),
            value: componentInfoData.tcProductComponentInfoYarnCount,
            props: { placeholder: "Enter yarn count" },
          },
          {
            label: "Component Info Fabric Weight and Construct",
            name: "tcProductComponentInfoFabricWeightAndConstruct",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: COMPONENT_FABRIC_CONSTRUCT_CATEGORIES.has(productCategoryCode),
            value: componentInfoData.tcProductComponentInfoFabricWeightAndConstruct,
            props: { placeholder: "Enter fabric weight and construct" },
          },
          {
            label: "Component Info Fabric Weight of Finished Product",
            name: "tcProductComponentInfoFabricWeightOfFinishedProduct",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required:
              COMPONENT_FINISHED_FABRIC_CATEGORIES.has(productCategoryCode) &&
              COMPONENT_FINISHED_FABRIC_DETAILS.has(productDetailCode),
            value: componentInfoData.tcProductComponentInfoFabricWeightOfFinishedProduct,
            props: { placeholder: "Enter fabric weight of finished product" },
          },
          {
            label: "Component Info Technical Specifications",
            name: "tcProductComponentInfoTechnicalSpecifications",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: componentInfoData.tcProductComponentInfoTechnicalSpecifications,
            props: { placeholder: "Enter technical specifications" },
          },
          {
            label: "Component Info Additional",
            name: "tcProductComponentInfoAdditional",
            type: "text",
            gridSize: { xs: 12, md: 12, lg: 12 },
            required: false,
            value: componentInfoData.tcProductComponentInfoAdditional,
            props: { placeholder: "Enter additional component info" },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setComponentInfoData(values);
    },
  };

  return (
    <FormBuilder
      formConfig={formConfig}
      onSubmit={formConfig.handleSubmit}
      formRef={formRef}
    />
  );
};

export default ComponentInfo;
